import React from 'react'
import { useAuth } from '@/features/auth'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Badge } from '@/shared/components/ui/badge'
import { User, Mail, Shield, Bell, CreditCard } from 'lucide-react'
import DashboardLayout from '@/features/dashboard/pages/DashboardLayout'

const AccountPage = () => {
    const { user } = useAuth()

    return (
        <DashboardLayout>
            <div className="p-6 max-w-4xl mx-auto space-y-8 animate-in fade-in duration-500">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-foreground to-foreground/50 bg-clip-text text-transparent">
                        Account Settings
                    </h1>
                    <p className="text-muted-foreground mt-2">
                        Manage your profile information and preferences.
                    </p>
                </div>

                <div className="grid gap-6 md:grid-cols-2">
                    {/* Profile Section */}
                    <Card className="border-sidebar-border bg-sidebar/50 backdrop-blur-sm">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="size-5 text-primary" />
                                Profile Information
                            </CardTitle>
                            <CardDescription>
                                Your personal details visible to others.
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center gap-4">
                                <Avatar className="size-20 border-2 border-primary/20">
                                    <AvatarImage src={user?.photoURL} />
                                    <AvatarFallback className="text-xl bg-primary/10">
                                        {user?.displayName?.charAt(0) || 'U'}
                                    </AvatarFallback>
                                </Avatar>
                                <Button variant="outline" size="sm">Change Avatar</Button>
                            </div>

                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="displayName">Display Name</Label>
                                    <Input
                                        id="displayName"
                                        defaultValue={user?.displayName || ''}
                                        className="bg-background/50 border-sidebar-border focus:ring-primary/20"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="email">Email Address</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            id="email"
                                            type="email"
                                            defaultValue={user?.email || ''}
                                            readOnly
                                            className="bg-muted border-sidebar-border cursor-not-allowed opacity-70"
                                        />
                                        <Badge variant="secondary" className="flex items-center gap-1 shrink-0 bg-primary/10 text-primary border-primary/20">
                                            <Shield className="size-3" />
                                            Verified
                                        </Badge>
                                    </div>
                                </div>
                            </div>
                            <Button className="w-full shadow-lg shadow-primary/20">Save Changes</Button>
                        </CardContent>
                    </Card>

                    {/* Preferences & Security */}
                    <div className="space-y-6">
                        <Card className="border-sidebar-border bg-sidebar/50 backdrop-blur-sm">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg">
                                    <Bell className="size-4 text-primary" />
                                    Notifications
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <p className="text-sm text-muted-foreground">
                                    Configure how you receive updates and alerts.
                                </p>
                                <Button variant="outline" className="w-full justify-start gap-2 border-sidebar-border hover:bg-sidebar-accent">
                                    <Mail className="size-4" />
                                    Email Notifications
                                </Button>
                            </CardContent>
                        </Card>

                        <Card className="border-sidebar-border bg-sidebar/50 backdrop-blur-sm">
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2 text-lg">
                                    <CreditCard className="size-4 text-primary" />
                                    Subscription
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-medium">Free Plan</span>
                                    <Badge variant="outline" className="border-primary/20 text-primary">Active</Badge>
                                </div>
                                <Button variant="secondary" className="w-full bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
                                    Upgrade to Pro
                                </Button>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                <Separator className="bg-sidebar-border" />

                <div className="flex justify-between items-center p-4 rounded-lg border border-destructive/20 bg-destructive/5">
                    <div>
                        <h4 className="font-semibold text-destructive">Danger Zone</h4>
                        <p className="text-sm text-muted-foreground">Permanently delete your account and all associated data.</p>
                    </div>
                    <Button variant="destructive">Delete Account</Button>
                </div>
            </div>
        </DashboardLayout>
    )
}

export default AccountPage
